// hooks, auth, navigation
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'auth/Auth';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// Material UI
import { Alert, Box, Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Snackbar } from "@mui/material";
import PearlLogoLoader from "components/PearlLogoLoader";
import CheckIcon from "@mui/icons-material/Check";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import StepForm from "./stepForm";
import ReviewStep from "./Review";
import stepsConfig from "./stepsConfig";
import PaymentTemplateModal from "layouts/Travel/payment-templates/PaymentTemplateModal";
import HighSeasonSurchargeModal from "layouts/Travel/hss/HighSeasonSurchargeModal";
import NewPackageComponentModal from "./NewPackageComponentModal";
import TripStatusSelector from "./wizard/components/TripStatusSelector";
import { applyTripStatusToFormData } from "./wizard/utils/tripStatus";
import { fetchGroupedCitiesForCountries } from "api/countriesNow";
import {previewPrivateObject} from "../../../utils/S3";
import { useScrollContainer } from "shell-v2/ScrollContainer";

const deepTrimStrings = (value) => {
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) return value.map(deepTrimStrings);
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value).map(([key, nestedValue]) => [key, deepTrimStrings(nestedValue)])
        );
    }
    return value;
};

const isEffectivelyEmpty = (value) =>
    value === undefined
    || value === null
    || (typeof value === 'string' && value.trim() === '')
    || (Array.isArray(value) && value.length === 0);

const TEXT_FORMAT_EXEMPT_KEYS = new Set([
    'primary_attraction',
    'location',
]);

// Initializing wizard stepper

const TripWizardStepper = ({ steps, activeStep, completedSteps, onStepClick, allStepsClickable = false }) => (
    <Box sx={{ width: "100%", overflowX: "auto", mt: 2.5, pt: 0.75, pb: 0.25 }}>
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                minWidth: { xs: 640, md: "100%" },
                px: 0.5,
            }}
        >
            {steps.map((label, index) => {
                const isCompleted = completedSteps.has(index) && index < activeStep;
                const isActive = activeStep === index;
                const isPast = index < activeStep || isCompleted;
                const isFirst = index === 0;
                const isLast = index === steps.length - 1;
                const lineBeforeBright = !isFirst && index <= activeStep;
                const lineAfterBright = !isLast && isPast;

                const isClickable = allStepsClickable || isCompleted || index <= activeStep;

                return (
                    <Box
                        key={label}
                        onClick={() => isClickable && onStepClick?.(index)}
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            minWidth: 56,
                            cursor: isClickable ? "pointer" : "default",
                            opacity: isClickable ? 1 : 0.6,
                        }}
                    >
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    height: 2,
                                    bgcolor: lineBeforeBright ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.28)",
                                    mr: isFirst ? 0 : -0.25,
                                }}
                            />
                            <Box
                                sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "0.8rem",
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    flexShrink: 0,
                                    position: "relative",
                                    zIndex: 2,
                                    transition: "all 0.2s ease",
                                    boxSizing: "border-box",
                                    ...(isCompleted
                                        ? {
                                            bgcolor: "#2E7D32",
                                            color: "#fff",
                                            border: "2px solid #2E7D32",
                                        }
                                        : isActive
                                            ? {
                                                bgcolor: "#fff",
                                                color: "#1565C8",
                                                border: "2px solid #fff",
                                            }
                                            : {
                                                bgcolor: "#0E2348",
                                                color: "#fff",
                                                border: "2px solid rgba(255,255,255,0.65)",
                                            }),
                                }}
                            >
                                {isCompleted ? <CheckIcon sx={{ fontSize: 18 }} /> : index + 1}
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    height: 2,
                                    bgcolor: lineAfterBright ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.28)",
                                    ml: isLast ? 0 : -0.25,
                                }}
                            />
                        </Box>
                        <MDTypography
                            variant="caption"
                            sx={{
                                mt: 1,
                                textAlign: "center",
                                textTransform: "uppercase",
                                fontSize: "0.62rem",
                                fontWeight: isActive ? 700 : 500,
                                letterSpacing: "0.05em",
                                color: isActive || isCompleted ? "#fff" : "rgba(255,255,255,0.72)",
                                lineHeight: 1.25,
                                maxWidth: 72,
                            }}
                        >
                            {label}
                        </MDTypography>
                    </Box>
                );
            })}
        </Box>
    </Box>
);

const CreateTrip = () => {
  // The window in classic; <main> in the v2 frame (see shell-v2/ScrollContainer).
  const scroller = useScrollContainer();

    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { editTripId } = useParams();
    const [searchParams] = useSearchParams();

    // State Management
    const [apiError, setApiError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [tripId, setTripId] = useState(null);
    const [tripHeroImage, setTripHeroImage] = useState('');
    const [errors, setErrors] = useState([])
    const [validationSnackbar, setValidationSnackbar] = useState({ open: false, message: '' });
    const [draftSnackbar, setDraftSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState(() => new Set());
    const [isTripLoading, setIsTripLoading] = useState(Boolean(editTripId));
    const [isStepDataLoading, setIsStepDataLoading] = useState(false);
    const [isContinuing, setIsContinuing] = useState(false);
    const wizard_steps = ['Overview', 'Packages', 'Pricing', "Trip Logistics", "Departures", "Itineraries", "Add-On", "FAQs", "Review"]
    const WIZARD_STEP_CONFIG_KEYS = [
        'overview', 'packages', 'pricing', 'services', 'departures',
        'itineraries', 'addons', 'faqs', 'review',
    ];
    const getStepConfigKey = (stepIndex) => WIZARD_STEP_CONFIG_KEYS[stepIndex] || 'overview';

    // Initializing step info

    const [formData, setFormData] = useState({
        overview: {},
        packages: {},
        pricing: {},
        services: {},
        departures: {},
        itineraries: {},
        addons: {},
        faqs: {},
    });
    const [tripStatus, setTripStatusState] = useState('draft');

    const handleTripStatusChange = (nextStatus) => {
        setTripStatusState(nextStatus);
        setFormData((prev) => applyTripStatusToFormData(prev, nextStatus));
    };

    // If editing any existing trips - checked via route

    useEffect(() => {
        if (editTripId) {
            fetchTrip(editTripId);
        } else {
            setIsTripLoading(false);
        }
    }, [editTripId]);

    // GET request if editing existing trip : autopopulate data

    const fetchTrip = async (id) => {
        setIsTripLoading(true);
        try {
            const response = await axiosInstance.get(`/trips/${id}`);
            const data = response.data;
            setTripId(data.id);
            const { base_price, currency, payment_templates, cities, city, ...packageRest } = data.packages || {};
            const normalizedPackageCities = Array.isArray(cities)
                ? cities
                : (typeof city === 'string' && city.trim()
                    ? city.split(',').map((item) => item.trim()).filter(Boolean)
                    : []);

            const trip_image = await resolveTripHeroImage(data.id)
            setTripHeroImage(trip_image || '');

            const loadedStatus = data.status || data.packages?.status || 'draft';
            setTripStatusState(loadedStatus);

            setFormData(applyTripStatusToFormData({
                overview: data.overview || {},
                packages: { ...packageRest, city: normalizedPackageCities },
                pricing: { base_price, currency, payment_templates },
                services: data.services || {},
                departures: data.departures || {},
                itineraries: data.itineraries || {},
                addons: data.addons || {},
                faqs: data.faqs || {},
            }, loadedStatus));
        } catch (error) {
            console.error('Error fetching trip:', error);
            setApiError('Unable to load trip details. Please refresh and try again.');
        } finally {
            setIsTripLoading(false);
        }
    };

    const resolveTripHeroImage = async (tripId) => {
        try {
            const response = await axiosInstance.get('/attachments/', {
                params: {
                    parent_table: 'travel_orders',
                    parent_id: tripId
                }
            });
            return await handlePreviewAttachment(response.data[0])

        } catch (err) {
            console.error(`Error fetching attachments for trip ${tripId}:`, err);
            return "";
        }
    };

    const handlePreviewAttachment = async (attachment) => {
        try {
            if (!attachment.bucket_name || !attachment.storage_path) {
                throw new Error('Attachment storage metadata is invalid for strict R2 download');
            }
            return await previewPrivateObject({
                axiosInstance,
                attachmentId: attachment.id,
            });
        } catch (error) {
            console.error('Error previewing attachment:', error);
        }
    };


    // An existing trip already has data for every step, so editing allows
    // jumping straight to any step instead of walking through them in order.
    const isEditMode = Boolean(editTripId);

    const handleStepClick = (index) => {
        if (index === activeStep) return;
        if (!isEditMode && !completedSteps.has(index) && index > activeStep) return;

        setErrors([]);
        setActiveStep(index);
        syncStepToUrl(index);
    };

    // Keep every wizard step anchored at top after navigation.
    useEffect(() => {
        scroller.scrollTo({ top: 0, behavior: "smooth" });
        if (isContinuing) {
            setIsContinuing(false);
        }
    }, [activeStep]);

    const syncStepToUrl = (stepIndex) => {
        const stepKey = getStepConfigKey(stepIndex);
        const nextParams = new URLSearchParams(searchParams);
        if (nextParams.get('step') === stepKey) return;
        nextParams.set('step', stepKey);
        navigate({ search: `?${nextParams.toString()}` }, { replace: true });
    };

    // State Management - Payment Template
    const [paymentTemplates, setPaymentTemplates] = useState([]);
    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);

    // State Management - HSS Template
    const [hssTemplates, setHssTemplates] = useState([]);
    const [hssModalOpen, setHssModalOpen] = useState(false);
    const hssSelectCallbackRef = useRef(null);

    // State Management - Trip Logistics

    const [tripComponents, setTripComponents] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [isCitiesLoading, setIsCitiesLoading] = useState(false);
    const [componentModalOpen, setComponentModalOpen] = useState(false);
    const componentSelectCallbackRef = useRef(null);

    // State Management - Itinerary Day

    const [itineraryDaysList, setItineraryDaysList] = useState([]);

    const selectedTripCountries = useMemo(() => {
        const primary = formData?.overview?.country ? [formData.overview.country] : [];
        const additional = Array.isArray(formData?.overview?.countries) ? formData.overview.countries : [];
        return Array.from(new Set([...primary, ...additional].filter(Boolean)));
    }, [formData?.overview?.country, formData?.overview?.countries]);

    // Trigger GET request for payment templates once user get on step 3 - Pricing on the wizard.

    useEffect(() => {
        if (wizard_steps[activeStep] === 'Pricing') {
            fetchPaymentTemplates()
        }

        else if (wizard_steps[activeStep] === 'Itineraries') {
            fetchTripComponents();
        }

        if (wizard_steps[activeStep] === 'Itineraries') {
            const duration = formData.packages?.duration || formData.pricing?.duration || 0;
            const existingDays = formData.itineraries?.days || [];

            // Build days array matching duration, preserving existing data
            const days = Array.from({ length: duration }, (_, i) => {
                return existingDays[i] || {
                    day_number: i + 1,
                    day_title: '',
                    // guide_instructions: '',
                    // description: '',
                    line_items: [],
                };
            });

            setFormData(prev => ({
                ...prev,
                itineraries: {
                    ...prev.itineraries,
                    days,
                }
            }));
        }

        if (wizard_steps[activeStep] === 'Add-On') {
            fetchItineraryDaysList();
            fetchTripComponents();
        }

    }, [activeStep])

    // GET trip components for Itineraries step in Wizard.

    const fetchTripComponents = async () => {
        try {
            if (!tripId) return;
            const response = await axiosInstance.get(`/trips/${tripId}`);
            // Map to id + label + capacity fields for addon min/max auto-fill
            const components = (response.data.services?.services || []).map((c, i) => ({
                id: c.id || i,
                label: `${c.service_name} (${c.category})`,
                category: c.category,
                min_occupancy: c.min_occupancy,
                max_occupancy: c.max_occupancy,
                min_passenger: c.min_passenger,
                max_passenger: c.max_passenger,
                minimum_participants: c.minimum_participants,
                maximum_participants: c.maximum_participants,
                cost_price: c.cost_price,
            }));
            setTripComponents(components);
        } catch (error) {
            console.error('Error fetching components:', error);
        }
    };

    // GET request for itineraries day list for Addons step

    const fetchItineraryDaysList = async () => {
        try {
            if (!tripId) return;
            const response = await axiosInstance.get(`/trips/${tripId}`);
            // Map to id + label for the dropdown
            const days = (response.data.itineraries?.days || []).map((d, i) => ({
                id: d.id,           // backend should return id here
                label: d.day_title ? `Day ${d.day_number}: ${d.day_title}` : `Day ${d.day_number}`,
            }));
            setItineraryDaysList(days);
        } catch (error) {
            console.error('Error fetching itineraries:', error);
        }
    };

    // GET request for payment templates for PRICING step in Wizard.

    const fetchPaymentTemplates = async () => {
        setIsStepDataLoading(true);
        try {
            const response = await axiosInstance.get('/payment-templates?status=active');
            setPaymentTemplates(response.data);
        } catch (error) {
            console.error('Error fetching payment templates:', error);
        } finally {
            setIsStepDataLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;

        const loadCities = async () => {
            if (!selectedTripCountries.length) {
                setCityOptions([]);
                return;
            }

            setIsCitiesLoading(true);
            try {
                // One country at a time — avoids geography request storms / DB pressure.
                const results = await fetchGroupedCitiesForCountries(
                    axiosInstance,
                    selectedTripCountries,
                    { concurrency: 1 }
                );
                if (cancelled) return;

                const merged = results
                    .flat()
                    .filter((item) => item?.city && item?.country)
                    .reduce((acc, item) => {
                        const key = `${item.country}::${item.state || 'Other'}::${item.city}`;
                        if (!acc.some((entry) => `${entry.country}::${entry.state || 'Other'}::${entry.city}` === key)) {
                            acc.push(item);
                        }
                        return acc;
                    }, [])
                    .sort((a, b) => {
                        const countryCmp = String(a.country).localeCompare(String(b.country));
                        if (countryCmp !== 0) return countryCmp;
                        const stateCmp = String(a.state || '').localeCompare(String(b.state || ''));
                        if (stateCmp !== 0) return stateCmp;
                        return String(a.city).localeCompare(String(b.city));
                    });
                setCityOptions(merged);
            } finally {
                if (!cancelled) setIsCitiesLoading(false);
            }
        };

        loadCities();
        return () => {
            cancelled = true;
        };
    }, [selectedTripCountries]);

    // Trigger GET request for HSS templates once user get on step 5 - Departures on the wizard.

    useEffect(() => {
        if (wizard_steps[activeStep] === 'Departures') {
            fetchHSSTemplates()
        }
    }, [activeStep])

    // GET request for payment templates for Departures step in Wizard.

    const fetchHSSTemplates = async () => {
        setIsStepDataLoading(true);
        try {
            const response = await axiosInstance.get('/hss?status=active');
            setHssTemplates(response.data);
        } catch (error) {
            console.error('Error fetching hss templates:', error);
        } finally {
            setIsStepDataLoading(false);
        }
    };

    // Open the HSS template modal from a departure row, remembering which row
    // asked for it so the new template can be selected there once created.

    const handleNewHssTemplate = (onCreated) => {
        hssSelectCallbackRef.current = typeof onCreated === 'function' ? onCreated : null;
        setHssModalOpen(true);
    };

    const closeHssModal = () => {
        hssSelectCallbackRef.current = null;
        setHssModalOpen(false);
    };

    const handleSaveHssTemplate = async (data) => {
        try {
            const response = await axiosInstance.post('/hss', data);
            const created = response.data;

            // A newly created template can be a draft, which the active-only
            // fetch would drop, so merge it into the list directly.
            if (created?.id) {
                setHssTemplates((prev) => [
                    ...prev.filter((template) => template.id !== created.id),
                    created,
                ]);
                hssSelectCallbackRef.current?.(created);
            }

            setDraftSnackbar({
                open: true,
                message: 'HSS template created successfully.',
                severity: 'success',
            });
            closeHssModal();
        } catch (error) {
            console.error('Error saving hss template:', error);
            setDraftSnackbar({
                open: true,
                message: 'Failed to save HSS template. Please try again.',
                severity: 'error',
            });
        }
    };

    // Update form data based on user input

    const handleChange = (key, value) => {
        // Updates the filled key value.
        const currentStepKey = getStepConfigKey(activeStep);
        const currentStepConfig = stepsConfig[currentStepKey];
        const field = currentStepConfig?.fields?.[key];

        const subFieldsToClear = {};
        if (field?.type !== 'repeatable-form') {
            field?.subFields?.forEach(sub => {
                Object.keys(sub.fields).forEach(subKey => {
                    subFieldsToClear[subKey] = '';
                });
            });
        }

        const nextValue = field?.type === 'repeatable-form' && Array.isArray(value)
            ? value.map((item) => ({ ...(item || {}) }))
            : value;

        setFormData(prev => {
            const merged = {
                ...(prev[currentStepKey] || {}),
                ...subFieldsToClear,
                [key]: nextValue,
            };
            return {
                ...prev,
                [currentStepKey]: applyDefaults(currentStepConfig?.fields || {}, merged),
            };
        });

        setErrors(prev => prev.filter(e => e.key !== key && !e.key.startsWith(`${key}.`)));

    };

    const scrollToFirstError = (errorList) => {
        if (!errorList?.length) return;
        window.requestAnimationFrame(() => {
            const firstErrorKey = errorList[0].key;
            const fieldEl = document.querySelector(`[data-trip-field="${firstErrorKey}"]`);
            if (fieldEl) {
                fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        });
    };

    const handleCancelTrip = () => {
        setCancelDialogOpen(true);
    };

    const handleCancelDialogClose = () => {
        setCancelDialogOpen(false);
    };

    const handleConfirmCancelTrip = () => {
        setCancelDialogOpen(false);
        navigate("/trips");
    };

    // Triggers when user hits continue or save as draft on the current wizard step.

    const handleContinue = async () => {
        if (isSaving || isContinuing) return;
        setIsContinuing(true);

        // If active step is last step, and user hits save, redirect back to list view.
        if (wizard_steps[activeStep] === 'Review') {
            await createUpdateTrip();
            navigate('/trips');
            setIsContinuing(false);
            return;
        }

        // Retrieve current step key and fields associated with the key
        const currentStepKey = getStepConfigKey(activeStep);
        const currentStepConfig = stepsConfig[currentStepKey];

        // Guard: skip validation if step has no fields yet
        if (!currentStepConfig?.fields) {
            await createUpdateTrip();
            setCompletedSteps((prev) => new Set([...prev, activeStep]));
            const nextStep = activeStep + 1;
            setActiveStep(nextStep);
            syncStepToUrl(nextStep);
            return;
        }

        // Recursively find missing required fields, including inside repeatable-form items.
        const findEmptyRequiredFields = (fieldsMap, data, prefix = '') => {
            let missing = [];
            const safeData = data || {};

            Object.entries(fieldsMap || {}).forEach(([key, field]) => {
                const errorKey = prefix ? `${prefix}.${key}` : key;

                if (field.type === 'repeatable-form') {
                    const items = safeData[key] || [];
                    items.forEach((item, index) => {
                        missing = missing.concat(
                            findEmptyRequiredFields(field.fields, item, `${errorKey}.${index}`)
                        );
                    });
                    return;
                }

                // Retrieve value
                const value = safeData[key];
                const isEmpty = isEffectivelyEmpty(value);

                // IF field is marked as required, and empty and not checkbox, raise required error.
                if (field.required && field.type !== 'checkbox' && isEmpty) {
                    missing.push({ key: errorKey, message: 'This field is required' });
                }

                // walk into conditional subFields if currently visible
                const matchedSub = field.subFields?.find(sub => {
                    if (sub.showWhen === true) return value === true;
                    if (sub.showWhen === '__has_value__') return !!value;
                    return sub.showWhen === value;
                });
                if (matchedSub) {
                    // subField values live in the same data object as the parent, same prefix
                    missing = missing.concat(findEmptyRequiredFields(matchedSub.fields, safeData, prefix));
                }
            });

            return missing;
        };

        const findFormatErrors = (fieldsMap, data, prefix = '') => {
            let formatErrors = [];
            const safeData = data || {};

            Object.entries(fieldsMap || {}).forEach(([key, field]) => {
                const errorKey = prefix ? `${prefix}.${key}` : key;
                const value = safeData[key];

                if (field.type === 'repeatable-form') {
                    (safeData[key] || []).forEach((item, index) => {
                        formatErrors = formatErrors.concat(
                            findFormatErrors(field.fields, item, `${errorKey}.${index}`)
                        );
                    });
                    return;
                }

                if (
                    field.type === 'text'
                    && typeof value === 'string'
                    && !TEXT_FORMAT_EXEMPT_KEYS.has(key)
                    && /\s{2,}/.test(value)
                ) {
                    formatErrors.push({
                        key: errorKey,
                        message: 'Remove extra spaces from this field',
                    });
                }

                const matchedSub = field.subFields?.find(sub => {
                    if (sub.showWhen === true) return value === true;
                    if (sub.showWhen === '__has_value__') return !!value;
                    return sub.showWhen === value;
                });
                if (matchedSub) {
                    formatErrors = formatErrors.concat(findFormatErrors(matchedSub.fields, safeData, prefix));
                }
            });

            return formatErrors;
        };

        const toComparableDate = (value) => {
            if (typeof value === 'string' && /^\d{2}:\d{2}$/.test(value)) {
                return new Date(`1970-01-01T${value}:00`);
            }
            return new Date(value);
        };

        const findItineraryErrors = (data) => {
            let errs = [];
            const days = data?.days || [];
            const lineItemFields = stepsConfig.itineraries?.lineItemFields || {};

            days.forEach((day, dayIndex) => {
                // ['day_title', 'description'].forEach((key) => {
                ['day_title'].forEach((key) => {
                    const value = day[key];
                    const isEmpty = isEffectivelyEmpty(value);
                    if (isEmpty) {
                        errs.push({ key: `days.${dayIndex}.${key}`, message: 'This field is required' });
                    }
                });

                const lineItems = day.line_items || [];

                // Per-field checks (required + validateAfter etc.)
                lineItems.forEach((item, itemIndex) => {
                    const itemPrefix = `days.${dayIndex}.line_items.${itemIndex}`;

                    Object.entries(lineItemFields).forEach(([key, field]) => {
                        const value = item[key];
                        const isEmpty = isEffectivelyEmpty(value);
                        const hasValue = !isEmpty;

                        if (field.required && isEmpty) {
                            errs.push({ key: `${itemPrefix}.${key}`, message: 'This field is required' });
                        }

                        if (field.validateMinExclusive !== undefined && hasValue) {
                            if (parseFloat(value) <= field.validateMinExclusive) {
                                errs.push({
                                    key: `${itemPrefix}.${key}`,
                                    message: field.validateMinExclusiveMessage
                                        || `Must be greater than ${field.validateMinExclusive}`,
                                });
                            }
                        }

                        if (field.validateMin !== undefined && hasValue) {
                            if (parseFloat(value) < field.validateMin) {
                                errs.push({
                                    key: `${itemPrefix}.${key}`,
                                    message: field.validateMinMessage || `Must be at least ${field.validateMin}`,
                                });
                            }
                        }

                        if (field.validateAfter && value && item[field.validateAfter]) {
                            if (toComparableDate(value) <= toComparableDate(item[field.validateAfter])) {
                                errs.push({
                                    key: `${itemPrefix}.${key}`,
                                    message: field.validateAfterMessage || 'Must be after the related field',
                                });
                            }
                        }

                        if (field.validateOnOrBefore && value && item[field.validateOnOrBefore]) {
                            if (toComparableDate(value) > toComparableDate(item[field.validateOnOrBefore])) {
                                errs.push({
                                    key: `${itemPrefix}.${key}`,
                                    message: field.validateOnOrBeforeMessage || 'Must not be later than the related field',
                                });
                            }
                        }
                    });
                });

                // NEW: sequence must be unique within this day's line items
                const sequenceCounts = {};
                lineItems.forEach((item) => {
                    const seq = item.sequence;
                    if (seq === undefined || seq === null || seq === '') return; // skip empty, required check already flags it
                    sequenceCounts[seq] = (sequenceCounts[seq] || 0) + 1;
                });

                lineItems.forEach((item, itemIndex) => {
                    const seq = item.sequence;
                    if (seq === undefined || seq === null || seq === '') return;
                    if (sequenceCounts[seq] > 1) {
                        errs.push({
                            key: `days.${dayIndex}.line_items.${itemIndex}.sequence`,
                            message: 'Sequence must be unique within this day',
                        });
                    }
                });
            });

            return errs;
        };

        // Addon price must be >= attached component price — disabled so users can enter any price.
        // const findAddonErrors = (data) => {
        //     const errs = [];
        //
        //     (data?.addons || []).forEach((addon, index) => {
        //         if (addon.addon_price === undefined || addon.addon_price === null || addon.addon_price === '') {
        //             return;
        //         }
        //         if (!addon.linked_component_id) return;
        //
        //         const component = (tripComponents || []).find(
        //             (c) => String(c.id) === String(addon.linked_component_id)
        //         );
        //         const componentPrice = parseFloat(component?.cost_price);
        //         if (!Number.isFinite(componentPrice)) return;
        //
        //         const price = parseFloat(addon.addon_price);
        //         if (Number.isFinite(price) && price < componentPrice) {
        //             errs.push({
        //                 key: `addons.${index}.addon_price`,
        //                 message: `Addon price must be greater than or equal to the attached component price (${componentPrice})`,
        //             });
        //         }
        //     });
        //
        //     return errs;
        // };

        const findCrossFieldErrors = (fieldsMap, data, prefix = '') => {
            let errors = [];
            const safeData = data || {};

            Object.entries(fieldsMap || {}).forEach(([key, field]) => {
                const errorKey = prefix ? `${prefix}.${key}` : key;
                const value = safeData[key];
                const compareValue = field.validateGreaterThan ?? field.validateGreaterThanOrEqual
                    ?? field.validateLessThan ?? field.validateLessThanOrEqual;
                const otherValue = compareValue ? safeData[compareValue] : undefined;

                const hasBothValues = !isEffectivelyEmpty(value) && !isEffectivelyEmpty(otherValue);

                const hasValue = !isEffectivelyEmpty(value);

                // --- Static (non-relative) bound checks ---

                // e.g. { validateMinExclusive: 0 } -> value must be strictly > 0
                if (field.validateMinExclusive !== undefined && hasValue) {
                    if (parseFloat(value) <= field.validateMinExclusive) {
                        errors.push({
                            key: errorKey,
                            message: field.validateMinExclusiveMessage
                                || `Must be greater than ${field.validateMinExclusive}`,
                        });
                    }
                }

                // e.g. { validateMin: 0 } -> value must be >= 0
                if (field.validateMin !== undefined && hasValue) {
                    if (parseFloat(value) < field.validateMin) {
                        errors.push({
                            key: errorKey,
                            message: field.validateMinMessage || `Must be at least ${field.validateMin}`,
                        });
                    }
                }

                // e.g. { validateMax: 100 } -> value must be <= 100
                if (field.validateMax !== undefined && hasValue) {
                    if (parseFloat(value) > field.validateMax) {
                        errors.push({
                            key: errorKey,
                            message: field.validateMaxMessage || `Must be no more than ${field.validateMax}`,
                        });
                    }
                }

                // e.g. { validatePhone: true } -> must look like a valid phone number
                if (field.validatePhone && hasValue) {
                    // allows digits, spaces, +, -, () — 7 to 15 digits total (loose international format)
                    const digitsOnly = String(value).replace(/\D/g, '');
                    const phoneShapeRegex = /^\+?[0-9\s\-()]{7,20}$/;

                    if (!phoneShapeRegex.test(value) || digitsOnly.length < 7 || digitsOnly.length > 15) {
                        errors.push({
                            key: errorKey,
                            message: field.validatePhoneMessage || 'Enter a valid contact number',
                        });
                    }
                }

                if (field.validateAfter && value && safeData[field.validateAfter]) {
                    if (toComparableDate(value) <= toComparableDate(safeData[field.validateAfter])) {
                        errors.push({ key: errorKey, message: field.validateAfterMessage || 'Must be after the related field' });
                    }
                }

                if (field.validateOnOrBefore && value && safeData[field.validateOnOrBefore]) {
                    if (toComparableDate(value) > toComparableDate(safeData[field.validateOnOrBefore])) {
                        errors.push({
                            key: errorKey,
                            message: field.validateOnOrBeforeMessage || 'Must not be later than the related field',
                        });
                    }
                }

                // strictly greater than
                if (field.validateGreaterThan && hasBothValues) {
                    if (parseFloat(value) <= parseFloat(safeData[field.validateGreaterThan])) {
                        errors.push({ key: errorKey, message: field.validateGreaterThanMessage || 'Must be greater than the related field' });
                    }
                }

                // greater than or equal to
                if (field.validateGreaterThanOrEqual && hasBothValues) {
                    if (parseFloat(value) < parseFloat(safeData[field.validateGreaterThanOrEqual])) {
                        errors.push({ key: errorKey, message: field.validateGreaterThanOrEqualMessage || 'Must be greater than or equal to the related field' });
                    }
                }

                // strictly less than
                if (field.validateLessThan && hasBothValues) {
                    if (parseFloat(value) >= parseFloat(safeData[field.validateLessThan])) {
                        errors.push({ key: errorKey, message: field.validateLessThanMessage || 'Must be less than the related field' });
                    }
                }

                // less than or equal to
                if (field.validateLessThanOrEqual && hasBothValues) {
                    if (parseFloat(value) > parseFloat(safeData[field.validateLessThanOrEqual])) {
                        errors.push({ key: errorKey, message: field.validateLessThanOrEqualMessage || 'Must be less than or equal to the related field' });
                    }
                }

                if (field.type === 'repeatable-form') {
                    (safeData[key] || []).forEach((item, index) => {
                        errors = errors.concat(findCrossFieldErrors(field.fields, item, `${errorKey}.${index}`));
                    });
                    return;
                }

                const matchedSub = field.subFields?.find(sub => {
                    if (sub.showWhen === true) return value === true;
                    if (sub.showWhen === '__has_value__') return !!value;
                    return sub.showWhen === value;
                });
                if (matchedSub) {
                    errors = errors.concat(findCrossFieldErrors(matchedSub.fields, safeData, prefix));
                }
            });

            return errors;
        };

        const trimmedFormData = deepTrimStrings(formData);

        // Retrieve only current step fields for validation.
        const stepData = trimmedFormData[currentStepKey];
        const emptyFields = findEmptyRequiredFields(currentStepConfig.fields, stepData);
        const formatErrors = findFormatErrors(currentStepConfig.fields, stepData);
        const crossFieldErrors = findCrossFieldErrors(currentStepConfig.fields, stepData);
        const itineraryErrors = currentStepKey === 'itineraries' ? findItineraryErrors(stepData) : [];
        // const addonErrors = currentStepKey === 'addons' ? findAddonErrors(stepData) : [];
        const allErrors = [...emptyFields, ...formatErrors, ...crossFieldErrors, ...itineraryErrors];

        if (allErrors.length > 0) {
            setErrors(allErrors);
            setValidationSnackbar({
                open: true,
                message: 'Please fill the required fields before continuing.',
            });
            scrollToFirstError(allErrors);
            setIsContinuing(false);
            return;
        }

        setFormData(trimmedFormData);

        // If the filled data are validated, proceed to next step.
        await createUpdateTrip(trimmedFormData);
        setCompletedSteps((prev) => new Set([...prev, activeStep]));
        const nextStep = activeStep + 1;
        setActiveStep(nextStep);
        syncStepToUrl(nextStep);
    };

    // POST Request if user creating new trip, else PUT request if updating existing.

    const createUpdateTrip = async (formDataOverride = null, { asDraft = false } = {}) => {
        setIsSaving(true);
        setApiError(null);
        try {
            const sourceFormData = deepTrimStrings(formDataOverride || formData);
            setFormData(sourceFormData);

            const defaultedFormData = Object.fromEntries(
                Object.entries(sourceFormData).map(([stepKey, stepData]) => {
                    const cfg = stepsConfig[stepKey];
                    return [stepKey, cfg?.fields ? applyDefaults(cfg.fields, stepData) : stepData];
                })
            );

            const effectiveStatus = asDraft ? 'draft' : (tripStatus || 'draft');
            const statusSyncedFormData = applyTripStatusToFormData(defaultedFormData, effectiveStatus);

            const pricingOnly = Object.fromEntries(
                Object.entries(statusSyncedFormData.pricing || {}).filter(
                    ([key]) => !(key in (statusSyncedFormData.packages || {}))
                )
            );

            const payload = sanitizePayload(
                {
                    ...statusSyncedFormData,
                    overview: {
                        ...statusSyncedFormData.overview,
                    },
                    packages: {
                        ...statusSyncedFormData.packages,
                        ...pricingOnly,
                        status: effectiveStatus,
                    },
                    pricing: undefined,
                    itineraries: isEmptyObject(statusSyncedFormData.itineraries) ? undefined : {
                        itinerary_title: statusSyncedFormData.itineraries.itinerary_title,
                        // itinerary_description: statusSyncedFormData.itineraries.itinerary_description,
                        // primary_attraction: statusSyncedFormData.itineraries.primary_attraction,
                        // meal_plan: statusSyncedFormData.itineraries.meal_plan,
                        inclusion: statusSyncedFormData.itineraries.inclusion,
                        exclusion: statusSyncedFormData.itineraries.exclusion,
                        status: effectiveStatus,
                        days: (statusSyncedFormData.itineraries.days || []).map(day => ({
                            id: day.id || undefined,
                            day_number: day.day_number,
                            day_title: day.day_title,
                            // description: day.description,
                            line_items: cleanRepeatable(day.line_items),
                        })),
                    },
                    status: effectiveStatus,
                }
            );

            if (tripId) {
                await axiosInstance.put(`/trips/${tripId}`, payload);
                return { success: true, tripId };
            }

            const response = await axiosInstance.post('/trips', payload);
            const newTripId = response.data.id;
            setTripId(newTripId);
            const stepParam = getStepConfigKey(activeStep);
            navigate(`/trips/${newTripId}/edit?step=${stepParam}`, { replace: true });
            return { success: true, tripId: newTripId };
        } catch (error) {
            setApiError('Something went wrong while saving. Please try again.');
            return { success: false };
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveDraft = async () => {
        const result = await createUpdateTrip(null, { asDraft: true });
        if (result?.success) {
            setTripStatusState('draft');
            setFormData((prev) => applyTripStatusToFormData(prev, 'draft'));
            setDraftSnackbar({
                open: true,
                message: 'Trip saved as draft.',
                severity: 'success',
            });
        }
    };

    // Utility to clean form data before sending

    const sanitizePayload = (obj) => {
        if (Array.isArray(obj)) return obj.map(sanitizePayload);
        if (obj && typeof obj === 'object') {
            return Object.fromEntries(
                Object.entries(obj)
                    .filter(([k]) => k !== '_clientKey')
                    .map(([k, v]) => [k, sanitizePayload(v)])
            );
        }
        return (typeof obj === 'string' && obj === '') ? null : obj;
    };

    // Check whether the value is empty or not

    const isEmptyObject = (val) =>
        !val || (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0);

    // Clean repeatable object

    const cleanRepeatable = (arr) => {
        if (!Array.isArray(arr)) return [];
        return arr.filter(item =>
            item &&
            typeof item === 'object' &&
            Object.keys(item).length > 0
        );
    };

    // Recursively apply field.default to any empty value, including inside
    // repeatable-form items and visible subFields.
    const applyDefaults = (fieldsMap, data) => {
        const result = { ...(data || {}) };

        Object.entries(fieldsMap || {}).forEach(([key, field]) => {
            if (field.type === 'repeatable-form') {
                const items = result[key] || [];
                // Apply per-item defaults without sharing references across items
                result[key] = items.map((item) => applyDefaults(field.fields, { ...(item || {}) }));
                return;
            }

            const isEmpty = result[key] === undefined || result[key] === null || result[key] === '';
            if (field.default !== undefined && isEmpty) {
                result[key] = field.default;
            }

            const matchedSub = field.subFields?.find(sub => {
                if (sub.showWhen === true) return result[key] === true;
                if (sub.showWhen === '__has_value__') return !!result[key];
                return sub.showWhen === result[key];
            });
            if (matchedSub) {
                Object.assign(result, applyDefaults(matchedSub.fields, result));
            }
        });

        return result;
    };

    // Trigger whenever step get updated

    useEffect(() => {
        // IF the activeStep is Pricing, fetch payment templates
        if (wizard_steps[activeStep] === 'Pricing') {
            fetchPaymentTemplates()
        }

        if (wizard_steps[activeStep] === 'Departures') {
            fetchHSSTemplates()
        }

        // IF the activeStep is Itineraries, fetch trip component
        if (wizard_steps[activeStep] === 'Itineraries') {
            fetchTripComponents();
            const duration = formData.packages?.duration || formData.pricing?.duration || 0;
            const existingDays = formData.itineraries?.days || [];

            // Build days array matching duration, preserving existing data
            const days = Array.from({ length: duration }, (_, i) => {
                return existingDays[i] || {
                    day_number: i + 1,
                    day_title: '',
                    // guide_instructions: '',
                    // description: '',
                    line_items: [],
                };
            });

            setFormData(prev => ({
                ...prev,
                itineraries: {
                    ...prev.itineraries,
                    days,
                }
            }));
        }

        if (wizard_steps[activeStep] === 'Add-On') {
            fetchItineraryDaysList();
            fetchTripComponents();
        }

        // apply configured defaults to the step we just landed on
        const stepKey = getStepConfigKey(activeStep);
        const stepConfig = stepsConfig[stepKey];
        if (stepConfig?.fields) {
            setFormData(prev => ({
                ...prev,
                [stepKey]: applyDefaults(stepConfig.fields, prev[stepKey]),
            }));
        }

    }, [activeStep])

    // Select | De-select Payment Template in the pricing step.

    const togglePaymentTemplateSelection = (key, templateId) => {
        setFormData(prev => {
            const pricing = prev.pricing || {};
            const current = pricing[key] || [];
            const updated = current.includes(templateId)
                ? current.filter(id => id !== templateId)
                : [...current, templateId];
            return {
                ...prev,
                pricing: {
                    ...pricing,
                    [key]: updated,
                }
            };
        });
    };

    // Handle create functionality for template.

    const handleNewTemplate = () => {
        setEditingTemplate(null);
        setTemplateModalOpen(true);
    };

    const handleNewComponent = (onCreated) => {
        componentSelectCallbackRef.current = typeof onCreated === 'function' ? onCreated : null;
        setComponentModalOpen(true);
    };

    const handleSaveNewComponent = async (componentData) => {
        const existingIds = new Set(
            (formData.services?.services || [])
                .map((s) => s.id)
                .filter((id) => id != null)
        );
        const nextFormData = {
            ...formData,
            services: {
                ...(formData.services || {}),
                services: [
                    ...(formData.services?.services || []),
                    componentData,
                ],
            },
        };

        setFormData(nextFormData);
        const result = await createUpdateTrip(nextFormData);
        if (!result?.success) {
            throw new Error('Failed to save component');
        }

        const savedTripId = result.tripId || tripId;
        if (!savedTripId) {
            throw new Error('Trip id missing after save');
        }

        const response = await axiosInstance.get(`/trips/${savedTripId}`);
        const savedServices = response.data.services?.services || [];
        setFormData((prev) => ({
            ...prev,
            services: response.data.services || prev.services,
        }));

        const components = savedServices.map((c, i) => ({
            id: c.id || i,
            label: `${c.service_name} (${c.category})`,
            category: c.category,
            min_occupancy: c.min_occupancy,
            max_occupancy: c.max_occupancy,
            min_passenger: c.min_passenger,
            max_passenger: c.max_passenger,
            minimum_participants: c.minimum_participants,
            maximum_participants: c.maximum_participants,
            cost_price: c.cost_price,
        }));
        setTripComponents(components);

        const created = [...savedServices]
            .reverse()
            .find(
                (s) =>
                    s.service_name === componentData.service_name &&
                    s.category === componentData.category &&
                    (s.id == null || !existingIds.has(s.id))
            ) || savedServices[savedServices.length - 1];

        if (created?.id != null && componentSelectCallbackRef.current) {
            componentSelectCallbackRef.current(created.id);
        }
        componentSelectCallbackRef.current = null;
        setComponentModalOpen(false);
    };

    // Handle edit functionality for existing template.

    const handleEditTemplate = (template) => {
        setEditingTemplate(template);
        setTemplateModalOpen(true);
    };

    // Handle Save functionality for a newly created or edited existing template.

    const handleSaveTemplate = async (data) => {
        try {
            if (editingTemplate) {
                await axiosInstance.put(`/payment-templates/${editingTemplate.id}`, data);
                setDraftSnackbar({
                    open: true,
                    message: 'Payment template updated successfully.',
                    severity: 'success',
                });
            } else {
                await axiosInstance.post('/payment-templates', data);
                setDraftSnackbar({
                    open: true,
                    message: 'Payment template created successfully.',
                    severity: 'success',
                });
            }
            fetchPaymentTemplates();
            setTemplateModalOpen(false);
        } catch (error) {
            console.error('Error saving template:', error);
            setDraftSnackbar({
                open: true,
                message: 'Failed to save payment template. Please try again.',
                severity: 'error',
            });
        }
    };

    // Update a day-level field (day_title, guide_instructions, description)

    const handleDayChange = (dayIndex, key, value) => {
        setFormData(prev => {
            const days = [...(prev.itineraries.days || [])];
            days[dayIndex] = { ...days[dayIndex], [key]: value };
            return { ...prev, itineraries: { ...prev.itineraries, days } };
        });
    };

    // Add empty line item to a specific day

    const handleAddLineItem = (dayIndex) => {
        setFormData(prev => {
            const days = [...(prev.itineraries.days || [])];
            const lineItems = [...(days[dayIndex].line_items || [])];
            const maxSequence = lineItems.reduce((max, item) => {
                const seq = parseInt(item.sequence, 10);
                return Number.isFinite(seq) ? Math.max(max, seq) : max;
            }, 0);
            lineItems.push({
                item_title: '',
                trip_component_map: null,
                location: '',
                // start_date_time: null,
                // end_date_time: null,
                sequence: maxSequence + 1,
                day_line_item_description: '',
            });
            days[dayIndex] = { ...days[dayIndex], line_items: lineItems };
            return { ...prev, itineraries: { ...prev.itineraries, days } };
        });
    };

    // Update a specific line item field

    const handleLineItemChange = (dayIndex, itemIndex, key, value) => {
        setFormData(prev => {
            const days = [...(prev.itineraries.days || [])];
            const lineItems = [...(days[dayIndex].line_items || [])];
            lineItems[itemIndex] = { ...lineItems[itemIndex], [key]: value };
            days[dayIndex] = { ...days[dayIndex], line_items: lineItems };
            return { ...prev, itineraries: { ...prev.itineraries, days } };
        });
    };

    // Remove a line item

    const handleRemoveLineItem = (dayIndex, itemIndex) => {
        setFormData(prev => {
            const days = [...(prev.itineraries.days || [])];
            const lineItems = days[dayIndex].line_items.filter((_, i) => i !== itemIndex);
            days[dayIndex] = { ...days[dayIndex], line_items: lineItems };
            return { ...prev, itineraries: { ...prev.itineraries, days } };
        });
    };

    const applyWizardStepFromQuery = (stepKey) => {
        if (!stepKey) return;
        const normalized = stepKey.toLowerCase();
        let stepIndex = WIZARD_STEP_CONFIG_KEYS.indexOf(normalized);
        if (stepIndex < 0) {
            stepIndex = wizard_steps.findIndex((step) => step.toLowerCase() === normalized);
        }
        if (stepIndex < 0) return;

        setActiveStep(stepIndex);
        setCompletedSteps(new Set(Array.from({ length: stepIndex }, (_, index) => index)));
    };

    useEffect(() => {
        if (isTripLoading) return;
        const stepKey = searchParams.get('step');
        if (stepKey) {
            applyWizardStepFromQuery(stepKey);
            return;
        }
        syncStepToUrl(activeStep);
    }, [isTripLoading, editTripId]);

    const pageTitle = editTripId ? "Edit Trip" : "Create Trip";

    const ensureTripId = async () => {
        if (tripId) return tripId;
        const result = await createUpdateTrip();
        return result?.success ? result.tripId : null;
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                sx={{ overflow: "visible" }}
                            >
                                <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                                    <MDBox>
                                        <MDTypography variant="h5" color="white">
                                            {pageTitle}
                                        </MDTypography>
                                        <MDTypography variant="caption" color="white" opacity={0.9} display="block" mt={0.5}>
                                            Step {activeStep + 1} of {wizard_steps.length} — {wizard_steps[activeStep]}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox display="flex" alignItems="center" gap={1.5} flexShrink={0} flexWrap="wrap">
                                        <TripStatusSelector
                                            value={tripStatus}
                                            onChange={handleTripStatusChange}
                                            disabled={isSaving || isContinuing}
                                        />
                                        {isSaving && (
                                            <>
                                                <PearlLogoLoader size="xs" showMessage={false} message="" />
                                                <MDTypography variant="caption" color="white">
                                                    Saving progress...
                                                </MDTypography>
                                            </>
                                        )}
                                        <MDButton variant="outlined" color="white" size="small" onClick={handleCancelTrip}>
                                            Cancel
                                        </MDButton>
                                    </MDBox>
                                </MDBox>

                                <TripWizardStepper
                                    steps={wizard_steps}
                                    activeStep={activeStep}
                                    completedSteps={completedSteps}
                                    onStepClick={handleStepClick}
                                    allStepsClickable={isEditMode}
                                />
                            </MDBox>

                            <MDBox p={3}>
                                {apiError && (
                                    <Alert severity="error" sx={{ mb: 3 }}>
                                        {apiError}
                                    </Alert>
                                )}

                                {isTripLoading ? (
                                    <MDBox py={8} display="flex" justifyContent="center">
                                        <PearlLogoLoader size="md" message="Loading trip details..." />
                                    </MDBox>
                                ) : wizard_steps[activeStep] === "Review" ? (
                                    <ReviewStep
                                        allFormData={formData}
                                        stepsConfig={stepsConfig}
                                        paymentTemplates={paymentTemplates}
                                        heroImage={tripHeroImage}
                                    />
                                ) : (
                                    <>
                                        {isStepDataLoading && (
                                            <MDBox mb={2} display="flex" alignItems="center" gap={1}>
                                                <PearlLogoLoader size="xs" showMessage={false} message="" />
                                                <MDTypography variant="caption" color="text">
                                                    Fetching latest data...
                                                </MDTypography>
                                            </MDBox>
                                        )}
                                        <StepForm
                                            tripId={tripId}
                                            ensureTripId={ensureTripId}
                                            initialImageUrl={tripHeroImage}
                                            basePrice={formData?.pricing?.base_price ?? formData?.packages?.base_price}
                                            stepConfig={stepsConfig[getStepConfigKey(activeStep)]}
                                            formData={formData[getStepConfigKey(activeStep)] || {}}
                                            onChange={handleChange}
                                            errors={errors}
                                            setErrors={setErrors}
                                            hssTemplates={hssTemplates}
                                            paymentTemplates={paymentTemplates}
                                            onToggleTemplate={togglePaymentTemplateSelection}
                                            onNewTemplate={handleNewTemplate}
                                            onEditTemplate={handleEditTemplate}
                                            duration={formData.packages?.duration || 0}
                                            itineraryDays={formData.itineraries?.days || []}
                                            onDayChange={handleDayChange}
                                            onAddLineItem={handleAddLineItem}
                                            onLineItemChange={handleLineItemChange}
                                            onRemoveLineItem={handleRemoveLineItem}
                                            tripComponents={tripComponents}
                                            itineraryDaysList={itineraryDaysList}
                                            cityOptions={cityOptions}
                                            isCitiesLoading={isCitiesLoading}
                                            onNewComponent={handleNewComponent}
                                            onNewHssTemplate={handleNewHssTemplate}
                                        />
                                    </>
                                )}

                                <PaymentTemplateModal
                                    open={templateModalOpen}
                                    onClose={() => setTemplateModalOpen(false)}
                                    onSave={handleSaveTemplate}
                                    template={editingTemplate}
                                />

                                <HighSeasonSurchargeModal
                                    open={hssModalOpen}
                                    onClose={closeHssModal}
                                    onSave={handleSaveHssTemplate}
                                    template={null}
                                />

                                <NewPackageComponentModal
                                    open={componentModalOpen}
                                    onClose={() => {
                                        componentSelectCallbackRef.current = null;
                                        setComponentModalOpen(false);
                                    }}
                                    onSave={handleSaveNewComponent}
                                    defaultCurrency={
                                        formData?.pricing?.currency ||
                                        formData?.packages?.currency ||
                                        ''
                                    }
                                />
                                <MDBox
                                    mt={3}
                                    pt={2}
                                    display="flex"
                                    justifyContent="space-between"
                                    gap={2}
                                    sx={{ borderTop: 1, borderColor: "divider" }}
                                >
                                    {activeStep > 0 && (
                                        <MDButton
                                            color="secondary"
                                            variant="outlined"
                                            disabled={isSaving || isContinuing}
                                            onClick={() => {
                                                setErrors([]);
                                                const prevStep = activeStep - 1;
                                                setActiveStep(prevStep);
                                                syncStepToUrl(prevStep);
                                            }}
                                        >
                                            Back
                                        </MDButton>
                                    )}

                                    <MDBox display="flex" gap={1} ml="auto">
                                        <MDButton
                                            color="dark"
                                            variant="outlined"
                                            disabled={isSaving || isContinuing}
                                            onClick={() => handleSaveDraft()}
                                            startIcon={<SaveOutlinedIcon />}
                                        >
                                            Save Draft
                                        </MDButton>
                                        <MDButton
                                            variant="gradient"
                                            color="info"
                                            disabled={isSaving || isContinuing}
                                            onClick={() => handleContinue()}
                                        >
                                            {isContinuing ? (
                                                <MDBox display="flex" alignItems="center" gap={1}>
                                                    <CircularProgress size={14} sx={{ color: "#fff !important" }} />
                                                    <MDTypography variant="button" sx={{ color: "#fff !important" }}>
                                                        Loading...
                                                    </MDTypography>
                                                </MDBox>
                                            ) : (
                                                <MDBox display="flex" alignItems="center" gap={1}>
                                                    <MDTypography variant="button" sx={{ color: "#fff !important" }}>
                                                        {activeStep === wizard_steps.length - 1 ? "Save" : "Continue"}
                                                    </MDTypography>
                                                    <ArrowForwardIcon sx={{ color: "#fff !important" }} />
                                                </MDBox>
                                            )}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <Snackbar
                open={validationSnackbar.open}
                autoHideDuration={5000}
                onClose={() => setValidationSnackbar({ open: false, message: '' })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity="error"
                    onClose={() => setValidationSnackbar({ open: false, message: '' })}
                >
                    {validationSnackbar.message}
                </Alert>
            </Snackbar>
            <Snackbar
                open={draftSnackbar.open}
                autoHideDuration={4000}
                onClose={() => setDraftSnackbar({ open: false, message: '', severity: 'success' })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={draftSnackbar.severity}
                    onClose={() => setDraftSnackbar({ open: false, message: '', severity: 'success' })}
                >
                    {draftSnackbar.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={cancelDialogOpen}
                onClose={handleCancelDialogClose}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Cancel trip creation?</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color="text">
                        Unsaved progress will be lost. Are you sure you want to cancel?
                    </MDTypography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={handleCancelDialogClose}
                    >
                        Keep Editing
                    </MDButton>
                    <MDButton
                        variant="gradient"
                        color="error"
                        onClick={handleConfirmCancelTrip}
                    >
                        Yes, Cancel
                    </MDButton>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}
export default CreateTrip;